import { CloseRounded } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';

type Props = {
  /**
   * 出す文言。null なら何も描かない。決め方は `progressToastLabel.ts`。
   */
  label: string | null;
  /**
   * 停止操作。渡さなければ停止ボタンを出さない。
   *
   * 確認ダイアログと GA は呼び出し側に置く。画面ごとに screen_name が違い、
   * 確認の器（ConfirmDialog）も画面が 1 つだけ持っているため。
   */
  onStop?: () => void;
};

/**
 * 右下に出す「実行中」のトースト。
 *
 * 大量の案件を投げた直後は画面が動かないので、押したことが効いているのか、
 * それとも固まったのかが分からない。進行中であることと、止める手段をここに集める。
 *
 * 見た目はカード配車（`ConvenienceDispatches/Presenter.tsx`）と `/map`
 * （`Map/Presenter.tsx`）が持っていたものをそのまま写した。長距離配車にも同じものが
 * 要るとユーザーから指摘があり（2026-09-29）、3 枚目を書き写す前に器へ出した。
 *
 * Why not 既存 2 画面もこれへ差し替える: 2 画面は出す条件（`/map` は「サーバが
 * 受け付けてから」、カード配車は「POST を投げた時点から」）と停止ボタンの出し分けが
 * 違い、どちらも本番で動いている。器だけ共有し、条件は呼び出し側に残す。
 */
export const DispatchProgressToast: FC<Props> = ({ label, onStop }) => {
  if (!label) return null;

  return (
    <Box
      data-testid="dispatch-progress-toast"
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: (theme) => theme.zIndex.snackbar + 10,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: 1,
        borderColor: 'divider',
        boxShadow: '0 10px 30px -10px rgba(0,0,0,0.4)',
        minWidth: 240,
        animation: 'progressToastSlideUp 0.2s ease-out',
        '@keyframes progressToastSlideUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <CircularProgress size={20} thickness={4} color="primary" />
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">しばらくお待ちください</Typography>
      </Box>
      {onStop && (
        <Tooltip title="自動配車の実行を停止する" arrow>
          <IconButton
            size="small"
            aria-label="自動配車の実行を停止する"
            onClick={onStop}
            sx={{ ml: 'auto', alignSelf: 'flex-start', mt: -0.5, mr: -0.5 }}
          >
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default DispatchProgressToast;
